import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

/**
 * Lightweight i18n — no dependency, localStorage-backed.
 *
 * Scope (deliberately pragmatic): UI chrome + recurring CTAs + nav. The
 * bulk of page copy comes from the admin CMS, which can grow its own
 * per-language fields later; this layer handles the shell so a TR/EN
 * switcher works today without a 2,000-string translation project.
 *
 * Usage:
 *   const { t, lang, setLang } = useI18n();
 *   <button>{t('cta.freeTrial')}</button>
 *
 * Missing keys fall back to English, then to the key itself, so a
 * partial dictionary never crashes the UI.
 */
const STORAGE_KEY = 'voipinger.lang';
const I18nContext = createContext({ lang: 'en', setLang: () => {}, t: (k) => k });

const DICT = {
  en: {
    'nav.home': 'Home',
    'nav.services': 'Services',
    'nav.features': 'Features',
    'nav.partners': 'Partners',
    'nav.company': 'Company',
    'nav.contact': 'Contact',
    'nav.login': 'Login',
    'cta.freeTrial': 'Free Trial',
    'cta.getStarted': 'Get started',
    'cta.talkToSpecialist': 'Talk to a specialist',
    'cta.bookDemo': 'Book a demo',
    'cta.viewPricing': 'View pricing',
    'cta.learnMore': 'Learn more',
    'cta.seeAll': 'See all',
    'footer.products': 'Products',
    'footer.company': 'Company',
    'footer.legal': 'Legal',
    'footer.rights': 'All rights reserved.',
    'footer.poweredBy': 'Powered by CLI Secure Ltd',
    'announce.freeTrial': 'Sign up now and get 14 days free — no credit card required.',
    'contact.namePlaceholder': 'Your name',
    'contact.emailPlaceholder': 'Business email',
    'contact.send': 'Send message',
  },
  tr: {
    'nav.home': 'Ana Sayfa',
    'nav.services': 'Hizmetler',
    'nav.features': 'Özellikler',
    'nav.partners': 'İş Ortakları',
    'nav.company': 'Kurumsal',
    'nav.contact': 'İletişim',
    'nav.login': 'Giriş',
    'cta.freeTrial': 'Ücretsiz Deneme',
    'cta.getStarted': 'Başlayın',
    'cta.talkToSpecialist': 'Uzmanla görüşün',
    'cta.bookDemo': 'Demo planlayın',
    'cta.viewPricing': 'Fiyatları görün',
    'cta.learnMore': 'Daha fazla',
    'cta.seeAll': 'Tümünü gör',
    'footer.products': 'Ürünler',
    'footer.company': 'Kurumsal',
    'footer.legal': 'Yasal',
    'footer.rights': 'Tüm hakları saklıdır.',
    'footer.poweredBy': 'CLI Secure Ltd tarafından sağlanır',
    'announce.freeTrial': 'Hemen kaydolun, 14 gün ücretsiz — kredi kartı gerekmez.',
    'contact.namePlaceholder': 'Adınız',
    'contact.emailPlaceholder': 'İş e-postanız',
    'contact.send': 'Mesaj gönder',
  },
};

export const LANGUAGES = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'tr', label: 'Türkçe', flag: '🇹🇷' },
];

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored && DICT[stored]) return stored;
      // Auto-detect from the browser on first visit.
      const nav = (navigator.language || 'en').slice(0, 2).toLowerCase();
      if (DICT[nav]) return nav;
    } catch (_) { /* ignore */ }
    return 'en';
  });

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, lang); } catch (_) { /* ignore */ }
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((l) => { if (DICT[l]) setLangState(l); }, []);

  const t = useCallback((key, fallback) => {
    return DICT[lang]?.[key] ?? DICT.en?.[key] ?? fallback ?? key;
  }, [lang]);

  return (
    <I18nContext.Provider value={{ lang, setLang, t }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  return useContext(I18nContext);
}
